/**
 * Build Day View itinerary items from Viator tours — mirrors iOS TourItemSaveHelper.
 */

import type { ItineraryItem } from '../types/client';
import {
  isMealProduct,
  type ViatorProduct,
} from '../services/viatorProductsService';

export type TourTimeSlot = 'morning' | 'afternoon' | 'evening' | 'night';

/** Default Viator tour duration (hours) — matches iOS TourItemSaveHelper. */
export const VIATOR_TOUR_DURATION_HOURS = 3;

export const TOUR_TIME_SLOTS: { value: TourTimeSlot; hour: number; minute: number }[] = [
  { value: 'morning', hour: 9, minute: 0 },
  { value: 'afternoon', hour: 14, minute: 0 },
  { value: 'evening', hour: 17, minute: 0 },
  { value: 'night', hour: 20, minute: 0 },
];

function parseYmd(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map((p) => Number.parseInt(p, 10));
  return new Date(y, m - 1, d);
}

function formatYmd(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Inclusive YYYY-MM-DD range from visit meta; falls back to [fallbackDate]. */
export function enumerateVisitDates(
  arrivalDate: string | null | undefined,
  departureDate: string | null | undefined,
  fallbackDate: string
): string[] {
  if (!arrivalDate) {
    return [fallbackDate];
  }
  const end = departureDate && departureDate >= arrivalDate ? departureDate : arrivalDate;
  const dates: string[] = [];
  let current = parseYmd(arrivalDate);
  const last = parseYmd(end);
  while (current.getTime() <= last.getTime()) {
    dates.push(formatYmd(current));
    current = new Date(current.getFullYear(), current.getMonth(), current.getDate() + 1);
  }
  return dates.length > 0 ? dates : [fallbackDate];
}

export function slotStartTime(slot: TourTimeSlot): string {
  const match = TOUR_TIME_SLOTS.find((s) => s.value === slot) ?? TOUR_TIME_SLOTS[0];
  return `${pad2(match.hour)}:${pad2(match.minute)}`;
}

export function buildTourItineraryItem(params: {
  tour: ViatorProduct;
  dateYmd: string;
  slot: TourTimeSlot;
  cityName?: string | null;
}): Omit<ItineraryItem, 'id'> {
  const { tour, slot, cityName } = params;
  const type = isMealProduct(tour) ? 'meal' : 'activity';
  const time = slotStartTime(slot);
  const notes = tour.description?.trim() ? tour.description.trim() : undefined;
  const url = tour.productUrl?.trim() || undefined;
  const price =
    typeof tour.price?.startingPrice === 'number' ? tour.price.startingPrice : null;
  const currency = tour.price?.currency?.trim() || null;

  return {
    title: tour.title,
    type,
    time,
    durationMinutes: VIATOR_TOUR_DURATION_HOURS * 60,
    notes,
    url,
    price,
    currency,
    location: cityName
      ? {
          address: cityName,
        }
      : undefined,
  };
}
